<template>
	<transition name="slide-fade">
		<div
			v-if="show || active"
			class="quick-tool-box-wrapper"
			:class="[
				{
					[`quick-tool-box-wrapper--default-${quickToolHeight}`]:
						!active && !error,
					[`quick-tool-box-wrapper--active-${quickToolHeight}`]:
						active,
					[`quick-tool-box-wrapper--error-${quickToolHeight}`]: error,
				},
			]"
		>
			<div class="quick-tool-box" @click="toggleQuickTool">
				<div class="quick-tool-box__building-wrapper">
					<div
						class="building"
						:class="[
							`icon-${iconName}-48`,
							{
								'offset-icon':
									action !== QuickToolActions.BUILD,
							},
						]"
					/>
				</div>
				<div
					class="quick-tool-box__text-wrapper"
					:class="{ 'quick-tool-box__text-wrapper--active': active }"
				>
					<ShrinkText
						:key="translate(text)"
						:font-size="14"
						:text="translate(text)"
						:max-line-limit="2"
						hasSameLineHeight
					/>
				</div>
			</div>
		</div>
	</transition>
</template>

<script lang="ts" setup>
import ShrinkText from "@/components/ShrinkText.vue";
import { FrameName } from "@/game/entities/config";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useQuickToolStore from "@/store/useQuickToolStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import { SkinTypes } from "@/types/ui";
import { QuickToolActions } from "@/types/upgrades";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

const emitter = markRaw(useEmitter());
const soundService = markRaw(SoundService.getInstance());
const sss = markRaw(useSiegeSystemStore());
const quickToolStore = useQuickToolStore();
const { show, active, error, building, action } = storeToRefs(quickToolStore);
const {
	setBuilding,
	setActive,
	setShow,
	setError,
	setAction,
	setHighwayStartCoordinates,
} = quickToolStore;

const quickToolHeight = ref<number>(70);

const toggleQuickTool = (): void => {
	if (active.value) {
		setBuilding(FrameName.NOT_SET);
		setActive(false);
		setShow(false);
		setError(false);
		setAction(QuickToolActions.NONE);
		setHighwayStartCoordinates(null);
		sss.setMovingUnitsIndex(null);
	} else {
		setActive(true);
		emitter.emit(EmitterEvents.PhaserRedirect);
	}

	soundService.playGenerickClick();
};

const text = computed((): string => {
	return active.value ? "ui.active" : "ui.quick_tool";
});

const iconName = computed((): string => {
	if (action.value === QuickToolActions.BUILD)
		return building.value.toLowerCase().replace(/\.png$/, "");
	return (skin.value + action.value).toLowerCase();
});

const skin = computed((): string => {
	if (active.value) return `${SkinTypes.DARK}-`;
	return `${SkinTypes.LIGHT}-`;
});
</script>

<style scoped lang="scss">
.quick-tool-box-wrapper {
	margin-right: 0.5rem;
	.quick-tool-box {
		@include flex-col-start;
		gap: 0.125rem;
		width: 100%;
		height: 100%;

		.offset-icon {
			margin-top: 0.25rem;
		}

		&__text-wrapper {
			display: flex;
			flex-grow: 1;
			justify-content: center;
			align-items: flex-end;
			padding: 0 0.375rem 0.125rem 0.375rem;
			width: 100%;
			overflow: hidden;
			color: #f2dcb5;
			font-weight: 800;
			font-size: 0.8125rem;
			line-height: 1;
			text-align: center;
			text-shadow: 0.04375rem 0.04375rem 0.0625rem rgba(0, 0, 0, 0.6);

			&--active {
				color: #372615;
				text-transform: uppercase;
			}

			:deep(.base) {
				// NOTE: Single line (default) has no specific padding because of align-items: flex-end.

				&.multi-line {
					padding-bottom: 0.275rem;
				}
			}
		}
	}

	.icon-placeholder-52 {
		clip-path: polygon(
			20% 0%,
			0% 20%,
			30% 50%,
			0% 80%,
			20% 100%,
			50% 70%,
			80% 100%,
			100% 80%,
			70% 50%,
			100% 20%,
			80% 0%,
			50% 30%
		);
		margin-top: 0.56875rem;
		background: red;
		width: 3.25rem;
		height: 3.25rem;
	}
}
.slide-fade-leave-active,
.slide-fade-enter-active {
	transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
	transform: translateX(2rem);
	opacity: 0;
}
</style>
